import type { FieldMeta } from './types';

const FT = {
  Text: 1,
  Number: 2,
  SingleSelect: 3,
  MultiSelect: 4,
  DateTime: 5,
  Checkbox: 7,
  User: 11,
  Phone: 13,
  Url: 15,
  Attachment: 17,
  SingleLink: 18,
  Lookup: 19,
  Formula: 20,
  DuplexLink: 21,
  CreatedTime: 1001,
  ModifiedTime: 1002,
  CreatedUser: 1003,
  ModifiedUser: 1004,
  AutoNumber: 1005,
  Barcode: 99001,
  Progress: 99002,
  Currency: 99003,
  Rating: 99004,
  Email: 99005,
} as const;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 结构性的键，抓文本时跳过，免得把 id/token 印到单据上。 */
const OPAQUE_KEYS = new Set([
  'id',
  'ids',
  'token',
  'type',
  'recordIds',
  'record_ids',
  'tableId',
  'table_id',
  'fieldId',
  'field_id',
  'timeZone',
  'time_zone',
  'size',
  'mimeType',
  'mime_type',
  'tmp_url',
  'url',
  'en_name',
  'avatar_url',
  'color',
]);

const READABLE_KEYS = ['full_address', 'fullAddress', 'address', 'name', 'text', 'title', 'link', 'value'];

/**
 * 最后一道兜底：任何值都不许渲染成 [object Object]。
 * 字段类型表跟不上飞书新增的类型（地理位置、群组等落到 default 分支），
 * 这里按形状把对象里可读的文本抓出来，抓不到就当空。
 */
function stringifyUnknown(value: unknown, depth = 0): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value !== 'object') return '';
  if (depth >= 3) return '';
  if (Array.isArray(value)) {
    return value
      .map((v) => stringifyUnknown(v, depth + 1))
      .filter(Boolean)
      .join(', ');
  }
  const obj = value as Record<string, unknown>;
  for (const key of READABLE_KEYS) {
    const v = obj[key];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return Object.keys(obj)
    .filter((k) => !OPAQUE_KEYS.has(k))
    .map((k) => stringifyUnknown(obj[k], depth + 1))
    .filter(Boolean)
    .join(' ');
}

function fallback(value: unknown): string {
  return stringifyUnknown(value);
}

/** 类型未知的原始值（渲染器里 meta 匹配不上的场合）走同一套兜底。 */
export function formatUnknownValue(value: unknown): string {
  return stringifyUnknown(value);
}

/** YYYY-MM-DD HH:mm — uses Intl.DateTimeFormat per spec (no dayjs) */
/**
 * 按字段配的日期格式输出。格式串来自 SDK（'yyyy/MM/dd'、'yyyy-MM-dd HH:mm' 等）。
 * 关键是：格式里没有 HH 就不能印时间 —— 一个「只显示日期」的字段在合同上
 * 印成「2026-05-25 00:00」，客户每次都要问那个 00:00 是什么意思。
 */
function formatTimestamp(ts: unknown, property?: FormatProperty): string {
  if (typeof ts !== 'number' || !Number.isFinite(ts)) return fallback(ts);
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const fmt = typeof property?.dateFormat === 'string' ? property.dateFormat : '';
  if (fmt) {
    return fmt
      .replace(/yyyy/g, yyyy)
      .replace(/MM/g, MM)
      .replace(/dd/g, dd)
      .replace(/HH/g, HH)
      .replace(/mm/g, mm);
  }
  return `${yyyy}-${MM}-${dd} ${HH}:${mm}`;
}

function formatTextSegments(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((seg) => (isObject(seg) && typeof seg.text === 'string' ? seg.text : ''))
      .join('');
  }
  return fallback(value);
}

function formatSelect(value: unknown): string {
  // SDK 给 {id,text}，OpenAPI（自动化授权码路线）直接给选项名字符串
  if (typeof value === 'string') return value;
  if (isObject(value) && typeof value.text === 'string') return value.text;
  return fallback(value);
}

function formatMultiSelect(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        if (typeof v === 'string') return v;
        return isObject(v) && typeof v.text === 'string' ? v.text : '';
      })
      .filter(Boolean)
      .join(', ');
  }
  return formatSelect(value);
}

function formatUsers(value: unknown): string {
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((u) => (isObject(u) && typeof u.name === 'string' ? u.name : ''))
    .filter(Boolean)
    .join(', ');
}

function formatUrl(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((seg) => {
        if (!isObject(seg)) return '';
        if (typeof seg.link === 'string' && seg.link) return seg.link;
        if (typeof seg.text === 'string') return seg.text;
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }
  if (isObject(value)) {
    if (typeof value.link === 'string' && value.link) return value.link;
    if (typeof value.text === 'string') return value.text;
  }
  return fallback(value);
}

function formatAttachment(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((a) => (isObject(a) && typeof a.name === 'string' ? a.name : ''))
      .filter(Boolean)
      .join('; ');
  }
  if (isObject(value) && typeof value.name === 'string') return value.name;
  return fallback(value);
}

function formatLink(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((v) => (isObject(v) && typeof v.text === 'string' ? v.text : ''))
      .filter(Boolean)
      .join(', ');
  }
  if (isObject(value) && typeof value.text === 'string') return value.text;
  return fallback(value);
}

function formatCheckbox(value: unknown): string {
  if (typeof value === 'boolean') return value ? '是' : '否';
  return fallback(value);
}

function formatPhoneOrEmail(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return formatTextSegments(value);
  if (isObject(value)) {
    if (typeof value.text === 'string') return value.text;
    if (typeof value.link === 'string') return value.link;
  }
  return fallback(value);
}

/**
 * 字段格式化配置。SDK 每种字段的 property 是各自的接口（结构互不兼容），
 * 所以 FieldMeta 上按 unknown 存，读取时在这里统一收口。
 */
interface FormatProperty {
  formatter?: string;
  decimalDigits?: number;
  currencyCode?: string;
  dateFormat?: string;
}

function readProperty(property: unknown): FormatProperty {
  return isObject(property) ? (property as FormatProperty) : {};
}

/** 常见货币的符号。查不到就用代码本身，总比不显示强。 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  CNY: '¥', USD: '$', EUR: '€', GBP: '£', JPY: '¥', HKD: 'HK$', TWD: 'NT$',
  KRW: '₩', AUD: 'A$', CAD: 'C$', SGD: 'S$', RUB: '₽', INR: '₹', THB: '฿',
};

function groupThousands(text: string): string {
  const [int, dec] = text.split('.');
  const sign = int.startsWith('-') ? '-' : '';
  const digits = sign ? int.slice(1) : int;
  return `${sign}${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${dec ? `.${dec}` : ''}`;
}

function decimalsOf(formatter: string): number {
  const m = /[.]([0#]+)/.exec(formatter);
  return m ? m[1].length : 0;
}

/**
 * 按字段配置的格式串输出数字。
 *
 * 不这么做的话，表里设成「百分比」的 0.125 在单据上印成 0.125、设成两位小数的
 * 金额印成 1234.5、公式算出来的 0.1+0.2 印成 0.30000000000000004 —— 客户拿着
 * 报价单来问「为什么和表里不一样」。
 */
function applyNumberFormatter(n: number, formatter: string): string {
  if (formatter.endsWith('%')) {
    const body = formatter.slice(0, -1);
    return `${(n * 100).toFixed(decimalsOf(body))}%`;
  }
  const text = n.toFixed(decimalsOf(formatter));
  const grouped = formatter.includes(',') ? groupThousands(text) : text;
  // '¥#,##0.00' 这种带前缀的格式串，前缀原样保留
  const prefix = /^[^\d#0,.-]+/.exec(formatter)?.[0] ?? '';
  return prefix + grouped;
}

/** 没有格式配置时的兜底：收掉浮点尾巴，别把 0.30000000000000004 印上单据。 */
function plainNumber(n: number): string {
  return String(Number(n.toFixed(6)));
}

function formatNumberLike(value: unknown, property?: FormatProperty): string {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    const formatter = typeof property?.formatter === 'string' ? property.formatter : '';
    if (formatter) return applyNumberFormatter(value, formatter);
    // 货币字段没有 formatter，给的是小数位 + 币种
    if (typeof property?.decimalDigits === 'number') {
      const symbol = property.currencyCode
        ? CURRENCY_SYMBOLS[property.currencyCode] ?? `${property.currencyCode} `
        : '';
      return symbol + groupThousands(value.toFixed(property.decimalDigits));
    }
    return plainNumber(value);
  }
  if (isObject(value) && 'value' in value) {
    return formatNumberLike(value.value, property);
  }
  return fallback(value);
}

/**
 * Best-effort shape probe — used for Lookup / Formula whose result type
 * isn't statically known from the field meta.
 */
/**
 * Lookup / Formula 的结果类型在字段元数据里拿不到，只能按形状猜。日期字段
 * 经由它们引用过来就是一个毫秒时间戳整数，原样印出来是一串 13 位数字
 * （客户在送货单上看到的就是 1700000000000）。按合理区间认成时间戳。
 * 下界 1e12 ≈ 2001-09，上界 1e13 ≈ 2286，普通业务数字不会落在这个区间。
 */
function looksLikeTimestamp(n: number): boolean {
  return Number.isInteger(n) && n >= 1e12 && n < 1e13;
}

function formatByShape(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return formatCheckbox(value);
  if (typeof value === 'number') {
    return looksLikeTimestamp(value) ? formatTimestamp(value) : formatNumberLike(value);
  }
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    const first = value[0];
    if (isObject(first)) {
      if ('name' in first && 'token' in first) return formatAttachment(value);
      if ('name' in first) return formatUsers(value);
      if ('link' in first) return formatUrl(value);
      if ('text' in first) {
        // text segments use empty separator; select-like values use ", "
        if (
          'type' in first &&
          (first.type === 'text' || first.type === 'url' || first.type === 'mention')
        ) {
          return formatTextSegments(value);
        }
        return formatMultiSelect(value);
      }
    }
    return value.map((v) => formatByShape(v)).filter(Boolean).join(', ');
  }
  if (isObject(value)) {
    if ('recordIds' in value && typeof value.text === 'string') return value.text;
    if ('name' in value && typeof value.name === 'string') return value.name;
    if ('text' in value && typeof value.text === 'string') return value.text;
    if ('link' in value && typeof value.link === 'string') return value.link;
    if ('value' in value) return formatByShape(value.value);
  }
  return fallback(value);
}

export function formatValue(
  fieldMeta: Pick<FieldMeta, 'type'> & { property?: unknown },
  rawValue: unknown,
): string {
  if (rawValue == null) return '';
  const property = readProperty(fieldMeta.property);

  switch (fieldMeta.type) {
    case FT.Text:
      return formatTextSegments(rawValue);

    case FT.Number:
    case FT.Currency:
    case FT.Progress:
    case FT.Rating:
      return formatNumberLike(rawValue, property);

    case FT.AutoNumber:
    case FT.Barcode:
      return formatTextSegments(rawValue) || formatNumberLike(rawValue, property);

    case FT.DateTime:
    case FT.CreatedTime:
    case FT.ModifiedTime:
      return formatTimestamp(rawValue, property);

    case FT.SingleSelect:
      return formatSelect(rawValue);

    case FT.MultiSelect:
      return formatMultiSelect(rawValue);

    case FT.User:
    case FT.CreatedUser:
    case FT.ModifiedUser:
      return formatUsers(rawValue);

    case FT.Checkbox:
      return formatCheckbox(rawValue);

    case FT.Url:
      return formatUrl(rawValue);

    case FT.Phone:
    case FT.Email:
      return formatPhoneOrEmail(rawValue);

    case FT.Attachment:
      return formatAttachment(rawValue);

    case FT.SingleLink:
    case FT.DuplexLink:
      return formatLink(rawValue);

    case FT.Lookup:
    case FT.Formula:
      return formatByShape(rawValue);

    default:
      return fallback(rawValue);
  }
}
